import { useState, useRef } from 'react';
import { RoiService } from '../../../services/RoiService';
import { RoiCanvasRef } from '../../../components/RoiCanvas';
import { EditMode } from '../types';

interface UseRoiEditingParams {
  roiData: any;
  setRoiData: (data: any) => void;
  selectedRoiFile: string;
  selectedCctv: string;
  projectId: string;
  setError: (msg: string) => void;
  setSuccess: (msg: string) => void;
  setHasUnsavedChanges: (v: boolean) => void;
  cctvRoiDataMap: Map<string, any>;
  setCctvRoiDataMap: React.Dispatch<React.SetStateAction<Map<string, any>>>;
  originalCctvDataMap: Map<string, any>;
  cctvWithChanges: Set<string>;
  setCctvWithChanges: React.Dispatch<React.SetStateAction<Set<string>>>;
  loadRoiLists: () => Promise<void>;
}

export function useRoiEditing({
  roiData,
  setRoiData,
  selectedRoiFile,
  selectedCctv,
  projectId,
  setError,
  setSuccess,
  setHasUnsavedChanges,
  cctvRoiDataMap,
  setCctvRoiDataMap,
  originalCctvDataMap,
  cctvWithChanges,
  setCctvWithChanges,
  loadRoiLists,
}: UseRoiEditingParams) {
  const [roiEditMode, setRoiEditMode] = useState<EditMode>(null);
  const [selectedRoiId, setSelectedRoiId] = useState<string>('');
  const roiCanvasRef = useRef<RoiCanvasRef>(null);

  const [showRoiIdDialog, setShowRoiIdDialog] = useState(false);
  const [newRoiId, setNewRoiId] = useState<string>('');
  const [pendingCreatePoints, setPendingCreatePoints] = useState<number[] | null>(null);
  const [currentDrawingPoints, setCurrentDrawingPoints] = useState<number[]>([]);

  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const [isSelectingRoiForEdit, setIsSelectingRoiForEdit] = useState(false);
  const [editingRoiId, setEditingRoiId] = useState<string>('');
  const [editingOriginalRoi, setEditingOriginalRoi] = useState<number[] | null>(null);

  // CCTV별 임시 저장
  const handleSave = () => {
    if (!selectedCctv || !roiData) return;
    console.log('💾 handleSave - Saving to local draft for CCTV:', selectedCctv);

    setCctvRoiDataMap(prevMap => {
      const newMap = new Map(prevMap);
      newMap.set(selectedCctv, { ...roiData });
      return newMap;
    });

    setCctvWithChanges(prevSet => {
      const newSet = new Set(prevSet);
      newSet.delete(selectedCctv);
      return newSet;
    });

    setSuccess(`${selectedCctv} 작업 내용이 초안에 저장되었습니다 (로컬)`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFinalSave = () => {
    setShowSaveDialog(true);
  };

  const handleConfirmSave = async () => {
    if (!selectedRoiFile) {
      setError('선택된 파일이 없습니다');
      return;
    }

    try {
      const baseFileName = selectedRoiFile.replace(/\.json$/, '').replace(/_\d+$/, '');
      await RoiService.saveDraftRoi(projectId, baseFileName);
      setSuccess(`${baseFileName} 파일로 저장 완료`);
      setHasUnsavedChanges(false);
      setCctvWithChanges(new Set());
      setShowSaveDialog(false);
      loadRoiLists();
    } catch (err) {
      setError('저장 실패: ' + (err as Error).message);
    }
  };

  const handleCreateRoi = (points: number[]) => {
    if (points.length < 6) {
      setError('최소 3개 이상의 점을 선택해주세요');
      return;
    }
    setPendingCreatePoints(points);

    const existingNumbers = Object.keys(roiData?.rois || {})
      .filter(id => id.startsWith('ParkingLocations_'))
      .map(id => parseInt(id.replace('ParkingLocations_', ''), 10))
      .filter(num => !isNaN(num));

    const nextNumber = existingNumbers.length > 0
      ? Math.max(...existingNumbers) + 1
      : 1;

    setNewRoiId(nextNumber.toString());
    setShowRoiIdDialog(true);
    setCurrentDrawingPoints([]);
  };

  const handleConfirmRoiId = async () => {
    if (!newRoiId.trim()) {
      setError('ROI 번호를 입력해주세요');
      return;
    }
    if (!roiData || !pendingCreatePoints || !selectedRoiFile || !selectedCctv) return;

    const number = parseInt(newRoiId.trim(), 10);
    if (isNaN(number) || number < 1) {
      setError('1 이상의 숫자를 입력해주세요');
      return;
    }

    const id = `ParkingLocations_${number}`;
    if (roiData.rois[id]) {
      setError('이미 존재하는 ROI 번호입니다');
      return;
    }

    const roundedPoints = pendingCreatePoints.map(Math.round);
    const newRois = { ...roiData.rois, [id]: roundedPoints };
    setRoiData({ ...roiData, rois: newRois });
    setRoiEditMode(null);
    setSelectedRoiId(id);
    setHasUnsavedChanges(true);

    try {
      const baseFileName = selectedRoiFile.replace(/\.json$/, '').replace(/_\d+$/, '');
      const imgWidth = 640;
      const imgHeight = 640;
      const originalCoords = roundedPoints.map((coord, index) =>
        index % 2 === 0 ? imgWidth - coord : imgHeight - coord
      );
      await RoiService.createRoi(projectId, {
        roi_id: id,
        cctv_id: selectedCctv,
        roi_file: baseFileName,
        coords: originalCoords
      });
    } catch (err) {
      console.error('Failed to create ROI in draft:', err);
      setError('ROI 생성 실패');
    }

    setShowRoiIdDialog(false);
    setPendingCreatePoints(null);
    setNewRoiId('');
  };

  const handleUpdateRoi = async (id: string, points: number[]) => {
    if (!roiData || !selectedRoiFile || !selectedCctv) return;

    const roundedPoints = points.map(Math.round);
    const newRois = { ...roiData.rois, [id]: roundedPoints };
    setRoiData({ ...roiData, rois: newRois });
    setRoiEditMode(null);
    setHasUnsavedChanges(true);

    try {
      const baseFileName = selectedRoiFile.replace(/\.json$/, '').replace(/_\d+$/, '');
      const imgWidth = 640;
      const imgHeight = 640;
      const originalCoords = roundedPoints.map((coord, index) =>
        index % 2 === 0 ? imgWidth - coord : imgHeight - coord
      );
      await RoiService.updateRoi(projectId, {
        roi_id: id,
        cctv_id: selectedCctv,
        roi_file: baseFileName,
        coords: originalCoords
      });
    } catch (err) {
      console.error('Failed to update ROI in draft:', err);
      setError('ROI 업데이트 실패');
    }

    setSelectedRoiId('');
    setEditingRoiId('');
    setEditingOriginalRoi(null);
    setCurrentDrawingPoints([]);
  };

  const handleConfirmDelete = async (id: string) => {
    if (!roiData || !selectedRoiFile || !selectedCctv) return;

    const newRois = { ...roiData.rois };
    delete newRois[id];
    setRoiData({ ...roiData, rois: newRois });
    setHasUnsavedChanges(true);

    try {
      const baseFileName = selectedRoiFile.replace(/\.json$/, '').replace(/_\d+$/, '');
      await RoiService.deleteRoi(projectId, {
        roi_id: id,
        cctv_id: selectedCctv,
        roi_file: baseFileName
      });
    } catch (err) {
      console.error('Failed to delete ROI in draft:', err);
      setError('ROI 삭제 실패');
    }

    setRoiEditMode(null);
    setEditingRoiId('');
    setSelectedRoiId('');
  };

  const handleRollback = () => {
    if (!selectedCctv) return;

    const originalData = originalCctvDataMap.get(selectedCctv);
    if (originalData) {
      setRoiData(JSON.parse(JSON.stringify(originalData)));

      setCctvRoiDataMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.set(selectedCctv, JSON.parse(JSON.stringify(originalData)));
        return newMap;
      });

      setCctvWithChanges(prevSet => {
        const newSet = new Set(prevSet);
        newSet.delete(selectedCctv);
        return newSet;
      });

      setCctvWithChanges(currentSet => {
        const hasOtherChanges = Array.from(currentSet).some(id => id !== selectedCctv);
        setHasUnsavedChanges(hasOtherChanges);
        return currentSet;
      });

      setSuccess(`${selectedCctv} CCTV의 변경사항이 롤백되었습니다`);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  return {
    roiEditMode,
    setRoiEditMode,
    selectedRoiId,
    setSelectedRoiId,
    roiCanvasRef,
    showRoiIdDialog,
    setShowRoiIdDialog,
    newRoiId,
    setNewRoiId,
    pendingCreatePoints,
    setPendingCreatePoints,
    currentDrawingPoints,
    setCurrentDrawingPoints,
    showSaveDialog,
    setShowSaveDialog,
    isSelectingRoiForEdit,
    setIsSelectingRoiForEdit,
    editingRoiId,
    setEditingRoiId,
    editingOriginalRoi,
    setEditingOriginalRoi,
    handleSave,
    handleFinalSave,
    handleConfirmSave,
    handleCreateRoi,
    handleConfirmRoiId,
    handleUpdateRoi,
    handleConfirmDelete,
    handleRollback,
  };
}
